import { Box, CircularProgress, Grid, IconButton, Link, Paper } from '@mui/material'
import { LargeCard } from 'Components/Cards/Large'
import { TextWithLabel } from 'Components/TextWithLabel'
import { LabelWithToolTip } from 'Components/Label/Index'
import { useEffect, useState } from 'react'
import { ConfigurationEdit } from 'Pages/ISDSettings/ConfigurationEdit'
import { type postError, type ISD } from 'core/types'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { sendGet, sendPut } from 'hooks/use-fetch'
import { useErrorDispatch } from 'ContextProviders/ErrorProvider'
import EditIcon from '@mui/icons-material/Edit'
import { InterestFormLanguageModal } from 'Pages/ISDSettings/InterestFormLanguageCustomization/InterestFormLanguageModal'

function FormatBoolean (value: boolean | null): string {
  if (value === undefined || value === null) {
    return ''
  } else if (value) {
    return 'true'
  } else {
    return 'false'
  }
}

function FormatAsMonthDay (d: Date | null | undefined): string {
  if (d === undefined || d === null) {
    return ''
  } else {
    const dateVal = new Date(d)
    const datestring = `${(dateVal.getMonth() + 1).toString().padStart(2, '0')}/${dateVal.getDate().toString().padStart(2, '0')}`
    return datestring
  }
}

export function Configuration (): JSX.Element {
  const isdID = useAccountId()?.id ?? 0
  const dispatch = useErrorDispatch()
  const [modalVisible, setModalVisible] = useState(false)
  const [interestFormEditGroup, setInterestFormEditGroup] = useState<number | null>(null)
  const [theIsd, setTheIsd] = useState<ISD>({} as unknown as ISD)
  const [loaded, setLoaded] = useState(false)
  const [refreshTime, setRefreshTime] = useState(new Date())
  const ifspTooltip = 'Enabling this setting will allow for an checkbox to capture whether a child has an IFSP on both the Guardian interest form and other places throughout the application'
  const iepTooltip = 'Enabling this setting will allow for an checkbox to capture whether a child has an IEP on both the Guardian interest form and other places throughout the application'
  const eoisTooltip = 'Enabling this setting will allow for an checkbox to capture whether a child has an EIOS on both the Guardian interest form and other places throughout the application'
  const otherCountiesTooltip = 'Enabling this setting will allow service provider facilities from other ISDs to show up in the browse providers section of the interest form'
  const intakeProviderTooltip = 'Enabling this setting will send all incoming interest form referrals to require intervention from the intake provider or ISD prior to being acted on by the selected provider(s)'
  const newProviderTooltip = 'Enabling this setting will allow a service provider to select another provider to create a referral for when the decline a referral'
  const caregiverPortalMaxProvidersTooltip = 'This setting will limit the number of providers a caregiver can select in the caregiver portal'

  useEffect(() => {
    const getIsd = async (): Promise<void> => {
      const { response: rsp } = await sendGet('/ISDDetails/GetISDConfig?id=' + isdID.toString(), {})
      if (rsp !== null) {
        setTheIsd(rsp)
        setLoaded(true)
      }
    }
    void getIsd()
  }, [isdID, refreshTime])

  const showModal = (): void => {
    setModalVisible(true)
  }

  const hideModal = (): void => {
    setModalVisible(false)
  }

  const handleSubmit = async (newValue: ISD): Promise<void> => {
    const { response } = await sendPut('/ISDDetails/ConfigureISD', newValue)
    const resp: { success: boolean, errors: postError[], newRecordID: number } = response
    if (resp.success) {
      setRefreshTime(new Date())
      setModalVisible(false)
      setTheIsd(newValue)
    } else {
      dispatch({ errors: resp.errors, type: 'update' })
    }
  }

  if (!loaded) return <CircularProgress />

  return (
    <>
      {modalVisible && (
        <ConfigurationEdit show={modalVisible} onClose={hideModal} isdSettings={theIsd} onConfirm={handleSubmit} />
      )}
      <Paper sx={{ pt: 2, borderRadius: '32px', marginTop: '20px', paddingLeft: '10px', paddingRight: '10px', paddingBottom: '10px' }}>
        <Grid container sx={{ display: 'flex', flexDirection: 'row' }} rowSpacing={2} columnSpacing={2}>
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
            <IconButton data-testid="editButton" onClick={showModal} >
              <EditIcon />
            </IconButton>
          </Grid>

          <Grid item xs={12} sm={12} lg={6} xl={6} >
            <LargeCard
              header='Interest Form Settings'
              fullWidth
              content={<Box>
                <TextWithLabel
                  label='Capture IFSP'
                  name='captureIFSP'
                  value={FormatBoolean(theIsd.captureIFSP)}
                  toolTip={ifspTooltip}
                />
                <TextWithLabel
                  label='Capture IEP'
                  name='captureIEP'
                  value={FormatBoolean(theIsd.captureIEP)}
                  toolTip={iepTooltip}
                />
                <TextWithLabel
                  label='Capture EOIS'
                  name='captureEOIS'
                  value={FormatBoolean(theIsd.captureEOIS)}
                  toolTip={eoisTooltip}
                />
                <TextWithLabel
                  label='Show results from other counties than the resident district'
                  name='showResultsFromOtherCounties'
                  value={FormatBoolean(theIsd.showResultsFromOtherCounties)}
                  toolTip={otherCountiesTooltip}
                />
                <TextWithLabel
                  label='Maximum selectable providers in caregiver portal'
                  name='caregiverPortalMaxProviders'
                  value={theIsd.caregiverPortalMaxProviders?.toString() ?? 'Unlimited'}
                  toolTip={caregiverPortalMaxProvidersTooltip}
                />
                <TextWithLabel
                  label='Show "Create a Referral"'
                  name='showCreateReferral'
                  value={FormatBoolean(theIsd.showCreateReferral)}
                />
                <TextWithLabel
                  label='Show "Services for Myself"'
                  name='showServicesForMyself'
                  value={FormatBoolean(theIsd.showServicesForMyself)}
                />
                <TextWithLabel
                  label='Services for Myself Name'
                  name='adultServicesName'
                  value={theIsd.adultServicesName}
                />
                <TextWithLabel
                  label='Services for Myself Link'
                  name='adultServicesLink'
                  value={theIsd.adultServicesLink}
                />
                <TextWithLabel
                  label='School Year Rollover Date'
                  name='schoolYearRolloverDate'
                  value={FormatAsMonthDay(theIsd.schoolYearRolloverDate)}
                />
                <TextWithLabel
                  label='Upcoming School Year Enrollment Opens'
                  name='upcomingSchoolYearOpenDate'
                  value={FormatAsMonthDay(theIsd.upcomingSchoolYearOpenDate)}
                />
              </Box>}
            />
          </Grid>
          <Grid item xs={12} sm={12} lg={6} xl={6} sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
            <Grid container sx={{ display: 'flex', flexDirection: 'column' }} rowSpacing={2} columnSpacing={2}>
              <Grid item sx={{ display: 'flex', gap: 2 }}>
                <LargeCard
                  header='Interest Form Customization'
                  fullWidth
                  content={<Box>
                    <Box sx={{ marginTop: '10px' }}>
                      <LabelWithToolTip labelText='Entry Point' sx={{ fontWeight: '600' }} toolTipText='Customize text on the entry point portion of the interest form' />
                      <Link
                        style={{ cursor: 'pointer' }}
                        data-testid="languageLink"
                        key={'languageLink'}
                        underline="hover"
                        onClick={() => { setInterestFormEditGroup(1) }}
                      >
                        Click here to customize the Entry Point language
                      </Link>
                    </Box>
                    <Box sx={{ marginTop: '10px' }}>
                      <LabelWithToolTip labelText='Long Form' sx={{ fontWeight: '600' }} toolTipText='Customize text on the long form portion of the interest form' />
                      <Link
                        style={{ cursor: 'pointer' }}
                        data-testid="languageLink"
                        key={'languageLink'}
                        underline="hover"
                        onClick={() => { setInterestFormEditGroup(2) }}
                      >
                        Click here to customize the Long Form language
                      </Link>
                    </Box>
                    <Box sx={{ marginTop: '10px' }}>
                      <LabelWithToolTip labelText='Short Form' sx={{ fontWeight: '600' }} toolTipText='Customize text on the short form portion of the interest form' />
                      <Link
                        style={{ cursor: 'pointer' }}
                        data-testid="languageLink"
                        key={'languageLink'}
                        underline="hover"
                        onClick={() => { setInterestFormEditGroup(3) }}
                      >
                        Click here to customize the Short Form language
                      </Link>
                    </Box>
                    <Box sx={{ marginTop: '10px' }}>
                      <LabelWithToolTip labelText='Referral' sx={{ fontWeight: '600' }} toolTipText='Customize text on the referral portion of the interest form' />
                      <Link
                        style={{ cursor: 'pointer' }}
                        data-testid="languageLink"
                        key={'languageLink'}
                        underline="hover"
                        onClick={() => { setInterestFormEditGroup(4) }}
                      >
                        Click here to customize the Referral language
                      </Link>
                    </Box>
                    <Box sx={{ marginTop: '10px' }}>
                      <LabelWithToolTip labelText='Login' sx={{ fontWeight: '600' }} toolTipText='Customize text on the login portion of the interest form' />
                      <Link
                        style={{ cursor: 'pointer' }}
                        data-testid="languageLink"
                        key={'languageLink'}
                        underline="hover"
                        onClick={() => { setInterestFormEditGroup(5) }}
                      >
                        Click here to customize the Login language
                      </Link>
                    </Box>
                    <Box sx={{ marginTop: '10px' }}>
                      <LabelWithToolTip labelText='Logged in Features' sx={{ fontWeight: '600' }} toolTipText='Customize text on the logged in features portion of the interest form' />
                      <Link
                        style={{ cursor: 'pointer' }}
                        data-testid="languageLink"
                        key={'languageLink'}
                        underline="hover"
                        onClick={() => { setInterestFormEditGroup(6) }}
                      >
                        Click here to customize the Logged in Features language
                      </Link>
                    </Box>
                    <Box sx={{ marginTop: '10px' }}>
                      <LabelWithToolTip labelText='Submission' sx={{ fontWeight: '600' }} toolTipText='Customize text on the submission portion of the interest form' />
                      <Link
                        style={{ cursor: 'pointer' }}
                        data-testid="languageLink"
                        key={'languageLink'}
                        underline="hover"
                        onClick={() => { setInterestFormEditGroup(7) }}
                      >
                        Click here to customize the Submission language
                      </Link>
                    </Box>
                    <Box sx={{ marginTop: '10px' }}>
                      <LabelWithToolTip labelText='Post Submit' sx={{ fontWeight: '600' }} toolTipText='Customize the greeting shown after the interest form is submitted' />
                      <Link
                        style={{ cursor: 'pointer' }}
                        data-testid="languageLink"
                        key={'languageLink'}
                        underline="hover"
                        onClick={() => { setInterestFormEditGroup(8) }}
                      >
                        Click here to customize the Post Submit language
                      </Link>
                    </Box>
                  </Box>}
                />
              </Grid>
              <Grid item sx={{ display: 'flex' }}>
                <LargeCard
                  header='Enrollment Process'
                  fullWidth
                  content={<Box>
                    <TextWithLabel
                      label='Auto refer to Intake Provider regardless of selected providers'
                      name='referAllToIntakeProvider'
                      value={theIsd.referAllToIntakeProvider ? 'true' : 'false'}
                      toolTip={intakeProviderTooltip}
                    />
                    <TextWithLabel
                      label='Allow provider to select new provider on referral decline'
                      name='selectProviderOnReferralDecline'
                      value={theIsd.selectProviderOnReferralDecline ? 'true' : 'false'}
                      toolTip={newProviderTooltip}
                    />

                  </Box>}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {interestFormEditGroup != null && <InterestFormLanguageModal
        isdid={isdID}
        open={true}
        onClose={() => { setInterestFormEditGroup(null) }}
        editGroup={interestFormEditGroup ?? 1}
      />}

    </>
  )
}
